// frontend/src/pages/ProfilePage.js - Betting stats + edit profile (parity with mobile ProfileScreen)
import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, User, TrendingUp, TrendingDown, Trophy, Flame, Target,
  Pencil, CheckCircle, AlertCircle, LogOut, ShieldCheck, ChevronRight
} from 'lucide-react';
import { formatNaira } from '../utils/money';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('playerbet_token')}`,
});

const ProfilePage = ({ user, onBack, onLogout, onUserUpdated, onNavigateToKyc }) => {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    username: user?.username || '',
    phoneNumber: user?.phoneNumber || '',
    address: user?.address || '',
    state: user?.state || '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type, text }

  const showMessage = (type, text) => {
    setMessage({ type, text });
    window.clearTimeout(showMessage._t);
    showMessage._t = window.setTimeout(() => setMessage(null), 6000);
  };

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await fetch(`${API_BASE_URL}/settlement/stats/${user.id}`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [user.id]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const data = await response.json();

      if (response.ok) {
        showMessage('success', 'Profile updated successfully');
        setEditing(false);
        // Keep localStorage + app state in sync
        try {
          const savedUser = JSON.parse(localStorage.getItem('playerbet_user')) || {};
          const updated = { ...savedUser, ...data.user };
          localStorage.setItem('playerbet_user', JSON.stringify(updated));
          if (onUserUpdated) onUserUpdated(updated);
        } catch (storageError) {
          console.error('Failed to update stored user:', storageError);
        }
      } else {
        showMessage('error', data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  const profitLoss = Number(stats?.profitLoss ?? 0);

  const statCards = stats ? [
    { label: 'Win Rate', value: `${stats.winRate ?? 0}%`, icon: Target, color: '#3b82f6' },
    { label: 'Total Bets', value: stats.totalBets ?? 0, icon: Trophy, color: '#a78bfa' },
    {
      label: 'Net Profit',
      value: formatNaira(profitLoss),
      icon: profitLoss < 0 ? TrendingDown : TrendingUp,
      color: profitLoss < 0 ? '#ef4444' : '#10b981',
    },
    { label: 'Total Wagered', value: formatNaira(stats.totalWagered), icon: TrendingUp, color: '#f59e0b' },
    { label: 'Biggest Win', value: formatNaira(stats.largestWin), icon: Trophy, color: '#10b981' },
    { label: 'Best Streak', value: `${stats.longestWinStreak ?? 0} wins`, icon: Flame, color: '#ef4444' },
  ] : [];

  const card = {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '16px',
  };

  const input = {
    width: '100%',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '12px',
    color: 'white',
    fontSize: '15px',
    boxSizing: 'border-box',
  };

  const label = {
    display: 'block',
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '6px',
    fontWeight: '500',
  };

  const field = (name, labelText, type = 'text') => (
    <div style={{ marginBottom: '14px' }}>
      <label style={label}>{labelText}</label>
      <input
        type={type}
        value={form[name]}
        onChange={e => setForm({ ...form, [name]: e.target.value })}
        style={input}
      />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}>
        <button
          onClick={onBack}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px',
            padding: '8px', color: 'white', cursor: 'pointer', display: 'flex',
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', fontWeight: 'bold', flexShrink: 0,
        }}>
          {user?.name ? user.name.charAt(0).toUpperCase() : <User size={24} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
            {user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`}
          </h1>
          <p style={{ fontSize: '13px', margin: '2px 0 0', opacity: 0.9 }}>
            @{user?.username}{memberSince ? ` · Member since ${memberSince}` : ''}
          </p>
        </div>
        <button
          onClick={onLogout}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px',
            padding: '8px 12px', color: 'white', cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600',
          }}
        >
          <LogOut size={14} /> Logout
        </button>
      </div>

      {/* Feedback banner */}
      {message && (
        <div style={{
          margin: '16px 20px 0',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
          border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
          color: message.type === 'success' ? '#10b981' : '#ef4444',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <div style={{ padding: '20px', maxWidth: '640px', margin: '0 auto' }}>
        {/* Verify Identity (KYC) */}
        {onNavigateToKyc && (
          <button
            onClick={onNavigateToKyc}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
              background: '#111827', border: '1px solid #374151', borderRadius: '12px',
              padding: '16px', cursor: 'pointer', color: 'white', textAlign: 'left',
              marginBottom: '20px',
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              backgroundColor: 'rgba(96,165,250,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldCheck size={20} color="#60a5fa" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>Verify Identity</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: 2 }}>
                Confirm your BVN/NIN to deposit &amp; withdraw
              </div>
            </div>
            <ChevronRight size={18} color="#6b7280" />
          </button>
        )}

        {/* Betting stats */}
        <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Betting Stats</h3>
        {loadingStats ? (
          <div style={{ ...card, textAlign: 'center', color: '#9ca3af' }}>Loading stats...</div>
        ) : !stats || (stats.totalBets ?? 0) === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: '32px 20px' }}>
            <Trophy size={42} color="#6b7280" style={{ marginBottom: '10px' }} />
            <h4 style={{ margin: '0 0 6px' }}>No Bets Yet</h4>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
              Place your first bet and your stats will show up here
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}>
            {statCards.map(({ label: statLabel, value, icon: Icon, color }) => (
              <div key={statLabel} style={{ ...card, marginBottom: 0, padding: '16px' }}>
                <Icon size={18} color={color} style={{ marginBottom: '8px' }} />
                <div style={{ fontSize: '17px', fontWeight: '700', color }}>{value}</div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{statLabel}</div>
              </div>
            ))}
          </div>
        )}

        {/* Record breakdown */}
        {stats && (stats.totalBets ?? 0) > 0 && (
          <div style={{ ...card, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
            {[
              { label: 'Won', value: stats.status?.won ?? 0, color: '#10b981' },
              { label: 'Lost', value: stats.status?.lost ?? 0, color: '#ef4444' },
              { label: 'Pending', value: stats.status?.pending ?? 0, color: '#f59e0b' },
            ].map(({ label: recordLabel, value, color }) => (
              <div key={recordLabel}>
                <div style={{ fontSize: '20px', fontWeight: '700', color }}>{value}</div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>{recordLabel}</div>
              </div>
            ))}
          </div>
        )}

        {/* Account details / edit */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 12px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Account Details</h3>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              style={{
                backgroundColor: 'rgba(124, 58, 237, 0.2)', border: '1px solid #7c3aed',
                borderRadius: '8px', padding: '8px 14px', color: 'white', fontSize: '13px',
                fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <Pencil size={14} /> Edit
            </button>
          )}
        </div>

        <div style={card}>
          {editing ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                {field('firstName', 'First Name')}
                {field('lastName', 'Last Name')}
              </div>
              {field('email', 'Email', 'email')}
              {field('username', 'Username')}
              {field('phoneNumber', 'Phone Number', 'tel')}
              {field('address', 'Address')}
              {field('state', 'State')}

              <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1,
                    background: saving ? '#374151' : 'linear-gradient(135deg, #7c3aed, #2563eb)',
                    border: 'none', borderRadius: '8px', padding: '12px', color: 'white',
                    fontSize: '15px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  style={{
                    backgroundColor: 'transparent', border: '1px solid #374151', borderRadius: '8px',
                    padding: '12px 20px', color: '#9ca3af', fontSize: '15px', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            [
              ['Name', `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name],
              ['Username', user?.username && `@${user.username}`],
              ['Email', user?.email],
              ['Phone', user?.phoneNumber],
              ['Address', [user?.address, user?.state].filter(Boolean).join(', ')],
            ].filter(([, value]) => value).map(([detailLabel, value]) => (
              <div
                key={detailLabel}
                style={{
                  display: 'flex', justifyContent: 'space-between', gap: '16px',
                  padding: '10px 0', borderBottom: '1px solid #374151', fontSize: '14px',
                }}
              >
                <span style={{ color: '#9ca3af', flexShrink: 0 }}>{detailLabel}</span>
                <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
